import React, { useState } from 'react';

import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGHADiscountSummary } from '~/hooks/apis/connection-google/useGHADiscountSummary';

import type {
  GHAAccountDiscountSummary,
  GHADiscountSummaryLine,
  GHAPropertyClassificationTier,
} from '~/services/ConnectionGoogleService';

import { ACCOUNT_LABELS, CLASSIFICATION_TIER_LABELS, CLASSIFICATION_TIER_OPTIONS } from './constants';
import {
  coveragePercent,
  formatCountry,
  formatDiscountKind,
  formatExclusionRule,
  formatExclusionRuleShort,
  formatRuleCoordinates,
  isClassificationTier,
  tierEntries,
} from './utils';

function TierChips({ byTier, size = 'small' }: { byTier: Record<string, number>; size?: 'small' | 'medium' }) {
  const entries = tierEntries(byTier);
  if (entries.length === 0) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {entries.map(([tier, label, count]) => (
        <Chip key={tier} size={size} variant="outlined" label={`${label} · ${count.toLocaleString()}`} />
      ))}
    </Stack>
  );
}

// One color per mechanism so "which kind of discount" reads at a glance across the whole page —
// the same visual language the GHA-429 rule grid will use for grants.
function DiscountKindChip({ line }: { line: GHADiscountSummaryLine }) {
  return (
    <Chip
      size="small"
      color={line.discountType === 'margin_retention' ? 'info' : 'success'}
      label={formatDiscountKind(line)}
    />
  );
}

function CoverageBar({ propertyCount, liveTotal }: { propertyCount: number; liveTotal: number }) {
  const percent = coveragePercent(propertyCount, liveTotal);
  if (percent == null) {
    return null;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <LinearProgress variant="determinate" value={percent} sx={{ width: 90, height: 6, borderRadius: 3 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {percent}%
      </Typography>
    </Stack>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
      {children}
    </Typography>
  );
}

function AccountHeader({ account }: { account: GHAAccountDiscountSummary }) {
  const accountLabel = ACCOUNT_LABELS[account.accountType] ?? account.accountType;
  return (
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" useFlexGap>
      <Typography variant="h6">{accountLabel}</Typography>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {account.liveProperties.total.toLocaleString()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          live properties
        </Typography>
      </Stack>
    </Stack>
  );
}

function AccountCard({ account }: { account: GHAAccountDiscountSummary }) {
  const hasDiscounts = account.discounts.length > 0;
  const hasBlocks = account.exclusionRules.length > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <AccountHeader account={account} />
        {account.ruleCoordinates.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Rule eligibility: {formatRuleCoordinates(account.ruleCoordinates)}
          </Typography>
        )}
        <Box sx={{ mt: 1.5 }}>
          <TierChips byTier={account.liveProperties.byTier} />
        </Box>

        {/* Slim card for accounts with nothing to report — no empty sections. */}
        {!hasDiscounts && !hasBlocks ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No active discounts.
          </Typography>
        ) : (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionLabel>Active discounts</SectionLabel>
            {hasDiscounts ? (
              <Table size="small" sx={{ mt: 0.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 130 }}>Country</TableCell>
                    <TableCell sx={{ width: 160 }}>Discount</TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>
                      Live properties
                    </TableCell>
                    <TableCell sx={{ width: 160 }}>
                      <Tooltip title="Share of the account's live properties holding this discount rule. Supplier-scoped blocks (below) can still suppress the discount on those suppliers' rates.">
                        <span>% with rule</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>Tier breakdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.discounts.map((line) => (
                    <TableRow
                      key={`${line.country ?? 'all'}-${line.discountType}-${line.discountValue}`}
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>{formatCountry(line.country)}</TableCell>
                      <TableCell>
                        <DiscountKindChip line={line} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {line.propertyCount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <CoverageBar propertyCount={line.propertyCount} liveTotal={account.liveProperties.total} />
                      </TableCell>
                      <TableCell>
                        <TierChips byTier={line.byTier} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No active discounts.
              </Typography>
            )}

            {hasBlocks && (
              <Box sx={{ mt: 2 }}>
                <SectionLabel>Blocked from discounting</SectionLabel>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {account.exclusionRules.map((line, index) => (
                    <Tooltip key={index} title={formatExclusionRule(line)}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        icon={<BlockIcon />}
                        label={formatExclusionRuleShort(line)}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function GHADiscountSummaryTable() {
  const [classificationTiers, setClassificationTiers] = useState<Array<GHAPropertyClassificationTier>>([]);
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useGHADiscountSummary({
    classificationTiers: classificationTiers.length === 0 ? undefined : classificationTiers,
  });

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading discount summary...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">Failed to load the GHA discount summary</Alert>
      </Box>
    );
  }

  // Accounts with nothing discounted (LPC, and ALT until it launches) are hidden entirely
  // rather than rendered as empty tiles.
  const accounts = (data ?? []).filter((account) => account.discounts.length > 0 || account.exclusionRules.length > 0);
  if (accounts.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No accounts with active discounts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="gha-discount-summary-tier">Tier</InputLabel>
            <Select
              labelId="gha-discount-summary-tier"
              label="Tier"
              multiple
              value={classificationTiers}
              onChange={(event) => {
                const values = Array.isArray(event.target.value) ? event.target.value : [event.target.value];
                if (values.includes('all')) {
                  setClassificationTiers([]);
                  return;
                }
                setClassificationTiers(values.filter(isClassificationTier));
              }}
              renderValue={(selected) => selected.map((value) => CLASSIFICATION_TIER_LABELS[value]).join(', ')}
            >
              <MenuItem value="all">All</MenuItem>
              {CLASSIFICATION_TIER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {CLASSIFICATION_TIER_LABELS[option]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            As of {new Date(dataUpdatedAt).toLocaleTimeString()}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        {accounts.map((account) => (
          <AccountCard key={account.accountType} account={account} />
        ))}
      </Stack>
    </Box>
  );
}
